import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Layers, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { CATEGORY_OPTIONS, CATEGORY_FIELDS, CATEGORY_COMMON_FIELDS, STOCK_STATUS, primaryIdentifier } from './categoryConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function DynamicField({ field, value, onChange }) {
  const common = "bg-slate-800 border-slate-700 text-white";
  if (field.type === 'select') {
    return (
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className={common} data-testid={`inv-${field.key}-select`}>
          <SelectValue placeholder={`Select ${field.label}`} />
        </SelectTrigger>
        <SelectContent className="bg-slate-800 border-slate-700 text-white">
          {field.options.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === 'textarea') {
    return <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} className={common} rows={2} data-testid={`inv-${field.key}-input`} />;
  }
  return (
    <Input type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
      value={value ?? ''} onChange={(e) => onChange(e.target.value)}
      required={!!field.required} className={common}
      data-testid={`inv-${field.key}-input`} />
  );
}

const empty = (category) => {
  const base = { product_type: category, stock_status: 'Available' };
  (CATEGORY_FIELDS[category] || []).forEach((f) => { base[f.key] = ''; });
  CATEGORY_COMMON_FIELDS.forEach((f) => { if (base[f.key] === undefined) base[f.key] = ''; });
  base.stock_status = 'Available';
  return base;
};

export default function InventoryManagement() {
  const { user } = useAuth();
  const canEdit = ['Super Admin', 'Admin', 'Branch Manager'].includes(user?.role);
  const canDelete = ['Super Admin', 'Admin'].includes(user?.role);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formCategory, setFormCategory] = useState('Mobile Phone');
  const [formData, setFormData] = useState(empty('Mobile Phone'));
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const openEdit = (item) => {
    setFormCategory(item.product_type);
    setFormData({ ...empty(item.product_type), ...item });
    setEditing(item);
    setDialogOpen(true);
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Delete ${item.product_id || 'product'}? Cannot be undone.`)) return;
    try {
      setDeleting(item.id);
      await axios.delete(`${API_URL}/api/inventory/delete/${item.id}`, { withCredentials: true });
      toast.success('Product deleted');
      fetchInventory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally { setDeleting(null); }
  };

  useEffect(() => { fetchInventory(); }, [categoryFilter, statusFilter]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.set('product_type', categoryFilter);
      if (statusFilter !== 'all') params.set('stock_status', statusFilter);
      const { data } = await axios.get(`${API_URL}/api/inventory?${params}`, { withCredentials: true });
      setInventory(data);
    } catch { toast.error('Failed to load inventory'); }
    finally { setLoading(false); }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return fetchInventory();
    try {
      const { data } = await axios.get(`${API_URL}/api/inventory/search?q=${encodeURIComponent(searchQuery)}`, { withCredentials: true });
      setInventory(data);
    } catch { toast.error('Search failed'); }
  };

  const changeCategory = (cat) => {
    setFormCategory(cat);
    setFormData(empty(cat));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const payload = { ...formData, product_type: formCategory };
      ['purchase_price', 'sale_price'].forEach((k) => { if (payload[k] !== '') payload[k] = Number(payload[k]); });
      if (payload.model_year) payload.model_year = Number(payload.model_year);
      Object.keys(payload).forEach((k) => { if (payload[k] === '') delete payload[k]; });
      if (editing) {
        await axios.put(`${API_URL}/api/inventory/update/${editing.id}`, payload, { withCredentials: true });
        toast.success(`${formCategory} updated`);
      } else {
        await axios.post(`${API_URL}/api/inventory`, payload, { withCredentials: true });
        toast.success(`${formCategory} added`);
      }
      setDialogOpen(false);
      setEditing(null);
      setFormData(empty(formCategory));
      fetchInventory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSubmitting(false); }
  };

  const categoryStats = useMemo(() => {
    const s = {};
    inventory.forEach((i) => { s[i.product_type] = (s[i.product_type] || 0) + 1; });
    return s;
  }, [inventory]);

  const activeFields = CATEGORY_FIELDS[formCategory] || [];

  return (
    <div className="space-y-6" data-testid="inventory-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="inventory-title">Multi-Category Inventory</h1>
          <p className="text-slate-400">Mobiles, Home Appliances, Motorcycles, Cars &amp; Properties — one warehouse, dynamic fields.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setFormData(empty(formCategory)); } }}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-inventory-button">
              <Plus className="h-4 w-4 mr-2" /> Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">{editing ? `Edit ${editing.product_id || editing.product_type}` : 'Add Inventory Item'}</DialogTitle>
              <DialogDescription className="text-slate-400">Category-specific fields appear below. Product ID is auto-generated on save.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-5 gap-2">
                {CATEGORY_OPTIONS.map((c) => (
                  <button type="button" key={c.value}
                    onClick={() => changeCategory(c.value)}
                    data-testid={`category-tab-${c.prefix}`}
                    className={`p-3 rounded-sm text-sm border transition-colors ${
                      formCategory === c.value
                        ? 'border-amber-500 bg-amber-500/10 text-amber-500'
                        : 'border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-600'
                    }`}>
                    <div className="font-bold">{c.prefix}</div>
                    <div className="text-xs mt-1">{c.label}</div>
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-800">
                {activeFields.map((f) => (
                  <div key={f.key} className="space-y-2">
                    <Label className="text-slate-300">
                      {f.label}{f.required && <span className="text-red-400"> *</span>}
                      {f.unique && <span className="text-xs text-amber-500 ml-1">(unique)</span>}
                    </Label>
                    <DynamicField field={f} value={formData[f.key]}
                      onChange={(v) => setFormData({ ...formData, [f.key]: v })} />
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-800">
                {CATEGORY_COMMON_FIELDS.map((f) => (
                  <div key={f.key} className="space-y-2">
                    <Label className="text-slate-300">
                      {f.label}{f.required && <span className="text-red-400"> *</span>}
                    </Label>
                    <DynamicField field={f} value={formData[f.key]}
                      onChange={(v) => setFormData({ ...formData, [f.key]: v })} />
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-2">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-300">Cancel</Button>
                <Button type="submit" disabled={submitting} data-testid="submit-inventory-button" className="bg-amber-500 hover:bg-amber-600">
                  {submitting ? 'Saving...' : editing ? `Update ${formCategory}` : `Add ${formCategory}`}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {CATEGORY_OPTIONS.map((c) => (
          <button key={c.value}
            onClick={() => setCategoryFilter((prev) => prev === c.value ? 'all' : c.value)}
            data-testid={`filter-category-${c.prefix}`}
            className={`p-3 rounded-sm border text-left transition-all ${
              categoryFilter === c.value
                ? 'border-amber-500 bg-amber-500/10'
                : 'border-slate-800 bg-slate-900 hover:border-slate-700'
            }`}>
            <div className="flex items-center justify-between">
              <span className="text-slate-400 text-xs uppercase tracking-wider">{c.prefix}</span>
              <Layers className="h-4 w-4 text-amber-500" />
            </div>
            <div className="text-2xl font-bold text-white mt-2">{categoryStats[c.value] || 0}</div>
            <div className="text-xs text-slate-400">{c.label}</div>
          </button>
        ))}
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:space-x-3 space-y-2 md:space-y-0">
            <div className="flex items-center space-x-2 flex-1">
              <Search className="h-5 w-5 text-slate-400" />
              <Input data-testid="search-inventory-input"
                placeholder="Search by Product ID, IMEI, Serial, Engine, Chassis, VIN, Registration..."
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="bg-slate-800 border-slate-700 text-white" />
              <Button onClick={handleSearch} data-testid="search-inventory-button" className="bg-amber-500 hover:bg-amber-600">Search</Button>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white w-full md:w-48" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white">
                <SelectItem value="all">All Statuses</SelectItem>
                {STOCK_STATUS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="text-center py-8 text-slate-400">Loading inventory...</div>
            : inventory.length === 0 ? (
              <div className="text-center py-8 text-slate-400" data-testid="no-inventory-message">No products found</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Product ID</TableHead>
                    <TableHead className="text-slate-400">Category</TableHead>
                    <TableHead className="text-slate-400">Brand / Model</TableHead>
                    <TableHead className="text-slate-400">Identifier</TableHead>
                    <TableHead className="text-slate-400">Purchase</TableHead>
                    <TableHead className="text-slate-400">Sale</TableHead>
                    <TableHead className="text-slate-400">Profit</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400 w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inventory.map((i) => (
                    <TableRow key={i.id} className="border-slate-800 hover:bg-slate-800/50" data-testid="inventory-row">
                      <TableCell className="text-amber-500 font-mono">{i.product_id || '-'}</TableCell>
                      <TableCell className="text-slate-300">{i.product_type}</TableCell>
                      <TableCell className="text-white">
                        {i.brand || i.property_type || '-'}{i.model ? ` — ${i.model}` : ''}
                        {i.appliance_category ? <span className="text-slate-400 text-xs ml-2">({i.appliance_category})</span> : null}
                        {i.society_project ? <span className="text-slate-400 text-xs ml-2">({i.society_project})</span> : null}
                      </TableCell>
                      <TableCell className="text-slate-300 text-xs font-mono">{primaryIdentifier(i)}</TableCell>
                      <TableCell className="text-slate-300">PKR {i.purchase_price?.toLocaleString()}</TableCell>
                      <TableCell className="text-slate-300">PKR {i.sale_price?.toLocaleString()}</TableCell>
                      <TableCell className="text-emerald-500">PKR {i.profit?.toLocaleString()}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-sm text-xs ${
                          i.stock_status === 'Available' ? 'bg-emerald-500/20 text-emerald-500' :
                          i.stock_status === 'Sold' ? 'bg-red-500/20 text-red-500' :
                          i.stock_status === 'Booked' ? 'bg-blue-500/20 text-blue-500' :
                          i.stock_status === 'Reserved' ? 'bg-amber-500/20 text-amber-500' :
                          'bg-slate-700/50 text-slate-400'
                        }`}>{i.stock_status || (i.is_sold ? 'Sold' : 'Available')}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          {canEdit && (
                            <Button size="sm" variant="ghost"
                              onClick={() => openEdit(i)}
                              data-testid={`edit-inventory-${i.id}`}
                              className="text-blue-400 hover:text-blue-300 hover:bg-slate-800 h-8 w-8 p-0" title="Edit">
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button size="sm" variant="ghost"
                              disabled={deleting === i.id}
                              onClick={() => handleDeleteItem(i)}
                              data-testid={`delete-inventory-${i.id}`}
                              className="text-red-400 hover:text-red-300 hover:bg-slate-800 h-8 w-8 p-0" title="Delete">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
